import {
  ENTRY_OFFSET_FACTOR,
  FIT_MARGIN,
  FRAME_COUNT,
  MIN_OVERFLOW_PX,
  NATIVE_H,
  NATIVE_W,
  P_BLANK_END,
  P_CROSSFADE_END,
  P_PAUSE_END,
  P_ROTATE_END,
  P_TEXT_HOLD_END,
  P_TEXT_IN_END,
  SCALE_MAX_BASE,
  SCALE_MAX_CEILING,
} from 'constants/dnaHub';
import { DnaFrameState } from 'types/dnaHub';
import { clamp, easeOutCubic, lerp } from 'utils/easing';

/** Direct port of `render(p)` from dna-prototype.html — see decision-log.md for the phase timeline. */
export const computeDnaFrameState = (p: number, viewportWidth: number, viewportHeight: number): DnaFrameState => {
  const scaleMax = clamp((viewportHeight + MIN_OVERFLOW_PX) / NATIVE_H, SCALE_MAX_BASE, SCALE_MAX_CEILING);

  let headOpacity: number;
  if (p < P_TEXT_IN_END) {
    headOpacity = easeOutCubic(clamp((p - P_BLANK_END) / (P_TEXT_IN_END - P_BLANK_END), 0, 1));
  } else if (p < P_TEXT_HOLD_END) {
    headOpacity = 1;
  } else {
    headOpacity = 1 - easeOutCubic(clamp((p - P_TEXT_HOLD_END) / (P_CROSSFADE_END - P_TEXT_HOLD_END), 0, 1));
  }

  const dnaOpacity = easeOutCubic(clamp((p - P_TEXT_HOLD_END) / (P_CROSSFADE_END - P_TEXT_HOLD_END), 0, 1));

  const tRot = clamp((p - P_TEXT_HOLD_END) / (P_ROTATE_END - P_TEXT_HOLD_END), 0, 1);
  const frameIndex = clamp(Math.round(tRot * (FRAME_COUNT - 1)), 0, FRAME_COUNT - 1);

  const overflow = Math.max(0, NATIVE_H * scaleMax - viewportHeight);
  const basePan = -tRot * overflow;

  const tEntry = easeOutCubic(clamp((p - P_TEXT_HOLD_END) / (P_CROSSFADE_END - P_TEXT_HOLD_END), 0, 1));
  const entryOffset = lerp(viewportHeight * ENTRY_OFFSET_FACTOR, 0, tEntry);

  const offsetYPan = basePan + entryOffset;

  const tOut = easeOutCubic(clamp((p - P_PAUSE_END) / (1 - P_PAUSE_END), 0, 1));
  const scaleFit = (viewportHeight * FIT_MARGIN) / NATIVE_H;
  const scale = lerp(scaleMax, scaleFit, tOut);

  const imgHeightEnd = NATIVE_H * scaleFit;
  const offsetYCentered = (viewportHeight - imgHeightEnd) / 2;
  const offsetY = lerp(offsetYPan, offsetYCentered, tOut);

  const width = NATIVE_W * (p < P_PAUSE_END ? scaleMax : scale);
  const height = NATIVE_H * (p < P_PAUSE_END ? scaleMax : scale);
  const x = (viewportWidth - width) / 2;
  const y = p < P_PAUSE_END ? offsetYPan : offsetY;

  return { headOpacity, dnaOpacity, frameIndex, x, y, width, height };
};

export const dnaFrameStatesEqual = (a: DnaFrameState, b: DnaFrameState): boolean =>
  a.headOpacity === b.headOpacity &&
  a.dnaOpacity === b.dnaOpacity &&
  a.frameIndex === b.frameIndex &&
  a.x === b.x &&
  a.y === b.y &&
  a.width === b.width &&
  a.height === b.height;
